import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Router, ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { EMPTY, Subject, catchError, filter, map, skip, switchMap, take, takeUntil } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ContentfulCollection, ContentfulService } from '../contentful/contentful.service';
import {
  ProductFields,
  formatWeightLabel,
} from '../contentful/product.model';
import { CartStore } from '../cart/cart.store';
import { SeoService } from '../seo/seo.service';
import { BANNER } from '../banner/banner';
import { CategoriesStore } from '../categories/categories.store';
import { CategoryNavComponent } from '../categories/category-nav.component';

const PAGE_SIZE = 24;

type PageItem = number | 'gap';

interface ProductRequest {
  categoryId?: string;
  page: number;
  scroll: boolean;
}

interface ProductCard {
  id: string;
  sku: string;
  name: string;
  imageUrl: string | null;
  price: number | null;
  wholesalePrice: number | null;
  package: string | null;
  units: string | null;
  weightLabel: string | null;
}

@Component({
  selector: 'app-home',
  imports: [DecimalPipe, RouterModule, MatSnackBarModule, CategoryNavComponent],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  products = signal<ProductCard[]>([]);
  page = signal(1);
  totalProducts = signal(0);
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.totalProducts() / PAGE_SIZE)));
  readonly pageItems = computed(() => buildPageItems(this.page(), this.pageCount()));
  readonly rangeLabel = computed(() => {
    const total = this.totalProducts();
    if (total === 0) return '';
    const start = (this.page() - 1) * PAGE_SIZE + 1;
    const end = Math.min(this.page() * PAGE_SIZE, total);
    return `${start}–${end} з ${total}`;
  });
  selectedCategory = signal<string | null>(null);
  selectedCategoryName = signal<string | null>(null);
  loadingProducts = signal(true);
  productsError = signal(false);

  readonly skeletonItems = Array.from({ length: 12 });
  addedIds = signal<ReadonlySet<string>>(new Set());

  private readonly destroy$ = new Subject<void>();
  private readonly productRequests$ = new Subject<ProductRequest>();
  private readonly snackBar = inject(MatSnackBar);
  private readonly seo = inject(SeoService);

  private readonly categoriesStore = inject(CategoriesStore);
  private readonly categoriesReady$ = toObservable(this.categoriesStore.loading);

  constructor(
    private readonly contentfulService: ContentfulService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cartStore: CartStore,
  ) {}

  ngOnInit(): void {
    this.publishHomeSeo();
    this.productRequests$
      .pipe(
        switchMap((request) => this.fetchProducts(request)),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => this.showProducts(result));

    this.categoriesReady$
      .pipe(filter((loading) => !loading), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.applyFromUrl(this.route.snapshot.queryParamMap));

    this.route.queryParamMap
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((params) => {
        if (!this.categoriesStore.loading()) this.applyFromUrl(params);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Apply the category and page from the URL without changing it. */
  private applyFromUrl(params: ParamMap): void {
    const slug = params.get('category');
    const page = parsePage(params.get('page'));
    const pageChanged = page !== this.page();
    const categoryChanged = slug !== this.selectedCategory();
    this.page.set(page);
    this.selectedCategory.set(slug);
    const cat = this.categoriesStore.bySlug(slug);
    this.selectedCategoryName.set(cat?.name ?? null);
    this.publishHomeSeo();
    this.loadProducts(cat?.id, page, pageChanged && !categoryChanged);
  }

  private publishHomeSeo(): void {
    this.seo.setHome(this.selectedCategoryName(), {
      url: BANNER.url,
      width: BANNER.width,
      height: BANNER.height,
    });
  }

  /** Called from the template when the user clicks a category. Updates URL + loads products. */
  selectCategory(slug: string | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { category: slug, page: null },
      queryParamsHandling: 'merge',
    });
  }

  pageQuery(page: number): Record<string, string | null> {
    return { page: page > 1 ? String(page) : null };
  }

  reloadProducts(): void {
    const slug = this.selectedCategory();
    const cat = this.categoriesStore.bySlug(slug);
    this.loadProducts(cat?.id, this.page(), false);
  }

  private loadProducts(categoryId: string | undefined, page: number, scroll: boolean): void {
    this.productRequests$.next({ categoryId, page, scroll });
  }

  private fetchProducts(request: ProductRequest) {
    this.loadingProducts.set(true);
    this.productsError.set(false);

    const query: Record<string, string | number> = { include: 1, order: 'fields.name' };
    if (request.categoryId) query['fields.category.sys.id'] = request.categoryId;

    return this.contentfulService
      .getEntries<ProductFields>({
        contentType: 'product',
        limit: PAGE_SIZE,
        skip: (request.page - 1) * PAGE_SIZE,
        query,
      })
      .pipe(
        map((res) => ({ res, request })),
        catchError(() => {
          this.productsError.set(true);
          this.loadingProducts.set(false);
          return EMPTY;
        }),
      );
  }

  private showProducts(result: { res: ContentfulCollection<ProductFields>; request: ProductRequest }): void {
    const { res, request } = result;
    const pageCount = Math.max(1, Math.ceil(res.total / PAGE_SIZE));
    if (request.page > pageCount) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { page: pageCount > 1 ? String(pageCount) : null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return;
    }

    const assetMap = new Map<string, string>();
    const inc = (res as unknown as { includes?: { Asset?: Array<{ sys: { id: string }; fields: { file?: { url?: string } } }> } }).includes;
    if (inc?.Asset) {
      for (const asset of inc.Asset) {
        const url = asset.fields?.file?.url;
        if (url) assetMap.set(asset.sys.id, url.startsWith('//') ? `https:${url}` : url);
      }
    }

    this.totalProducts.set(res.total);
    this.products.set(
      res.items.map((item) => {
        const f = item.fields;
        const firstImgId = f.images?.[0]?.sys?.id;
        const units = f.units == null ? null : String(f.units);
        return {
          id: item.sys.id,
          name: f.name,
          sku: f.sku ?? item.sys.id,
          imageUrl: firstImgId ? (assetMap.get(firstImgId) ?? null) : null,
          price: f.price ?? null,
          wholesalePrice: f.wholesalePrice ?? null,
          package: f.package ?? null,
          units,
          weightLabel: formatWeightLabel(f.weight, units),
        };
      }),
    );
    this.loadingProducts.set(false);
    if (request.scroll) this.scrollToCatalog();
  }

  private scrollToCatalog(): void {
    if (typeof document === 'undefined') return;
    document.getElementById('catalog')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  cartQty(product: ProductCard): number {
    return this.cartStore.quantityOf(product.id, product.weightLabel);
  }

  changeCartQty(product: ProductCard, delta: number): void {
    const next = this.cartQty(product) + delta;
    this.cartStore.updateQty(product.id, product.weightLabel, next);
  }

  addToCart(product: ProductCard): void {
    this.cartStore.add({
      productId:   product.id,
      sku:         product.sku,
      name:        product.name,
      imageUrl:    product.imageUrl,
      weightLabel: product.weightLabel,
      unit:        product.units,
      price:       product.price,
    });

    this.addedIds.update((ids) => new Set(ids).add(product.id));
    setTimeout(() => {
      this.addedIds.update((ids) => {
        const next = new Set(ids);
        next.delete(product.id);
        return next;
      });
    }, 900);

    this.snackBar.open(`${product.name} додано до кошика`, 'OK', {
      duration: 2500,
      panelClass: 'snack-success',
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
    });
  }

  isAdded(productId: string): boolean {
    return this.addedIds().has(productId);
  }
}

function parsePage(value: string | null): number {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : 1;
}

function buildPageItems(current: number, count: number): PageItem[] {
  if (count <= 7) return Array.from({ length: count }, (_, index) => index + 1);

  const items: PageItem[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(count - 1, current + 1);
  if (start > 2) items.push('gap');
  for (let page = start; page <= end; page++) items.push(page);
  if (end < count - 1) items.push('gap');
  items.push(count);
  return items;
}
